import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { MessageSquareIcon, ArchiveIcon, UserIcon, PlusIcon } from '../components/Icons';

interface Conversation {
  id: number;
  participantName: string;
  participantRole: 'writer' | 'reader' | 'agent';
  participantPhoto: string;
  lastMessage: string;
  lastMessageTime: string;
  unread: boolean;
  archived: boolean;
  subject?: string;
}

export const ConversationsListPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Mock data
  const conversations: Conversation[] = [
    {
      id: 1,
      participantName: 'Sarah Johnson',
      participantRole: 'agent',
      participantPhoto: '',
      lastMessage: 'Thanks for sending the revised pages. I\'ll take a look this week!',
      lastMessageTime: '2 hours ago',
      unread: true,
      archived: false,
      subject: 'Re: Manuscript Revisions',
    },
    {
      id: 2,
      participantName: 'Emma Rodriguez',
      participantRole: 'reader',
      participantPhoto: '',
      lastMessage: 'I finished reading chapter 5! Really enjoying the story so far.',
      lastMessageTime: '5 hours ago',
      unread: true,
      archived: false,
      subject: 'Feedback on The Silent Shore',
    },
    {
      id: 3,
      participantName: 'Michael Chen',
      participantRole: 'writer',
      participantPhoto: '',
      lastMessage: 'I appreciate your feedback on the opening chapter. Very helpful!',
      lastMessageTime: '1 day ago',
      unread: false,
      archived: false,
      subject: 'Beta Reading Exchange',
    },
    {
      id: 4,
      participantName: 'Jennifer Williams',
      participantRole: 'agent',
      participantPhoto: '',
      lastMessage: 'Looking forward to reading your full manuscript.',
      lastMessageTime: '2 days ago',
      unread: false,
      archived: false,
      subject: 'Full Manuscript Request',
    },
    {
      id: 5,
      participantName: 'David Park',
      participantRole: 'reader',
      participantPhoto: '',
      lastMessage: 'Thanks for the opportunity to beta read. When do you need feedback by?',
      lastMessageTime: '3 days ago',
      unread: false,
      archived: false,
      subject: 'Beta Reading Timeline',
    },
    {
      id: 6,
      participantName: 'Alex Martinez',
      participantRole: 'writer',
      participantPhoto: '',
      lastMessage: 'I\'d love to swap manuscripts for beta reading!',
      lastMessageTime: '1 week ago',
      unread: false,
      archived: true,
      subject: 'Beta Reading Swap',
    },
  ];

  const getRoleBadgeVariant = (role: string): 'default' | 'success' | 'warning' | 'error' | 'primary' => {
    switch (role) {
      case 'agent':
        return 'primary';
      case 'reader':
        return 'success';
      case 'writer':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string): string => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const handleArchive = (conversationId: number) => {
    console.log('Archiving conversation:', conversationId);
    window.alert('Conversation archived');
  };

  const handleUnarchive = (conversationId: number) => {
    console.log('Unarchiving conversation:', conversationId);
    window.alert('Conversation unarchived');
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.participantName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         conv.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         conv.lastMessage.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesArchiveFilter = showArchived ? conv.archived : !conv.archived;
    return matchesSearch && matchesArchiveFilter;
  });

  const unreadCount = conversations.filter(c => c.unread && !c.archived).length;
  const archivedCount = conversations.filter(c => c.archived).length;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '32px',
        }}>
          <div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              Messages
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              {unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
              {archivedCount > 0 && ` • ${archivedCount} archived`}
            </p>
          </div>

          <Button onClick={() => window.location.href = '/messages/new'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PlusIcon width={16} height={16} />
              <span>New Message</span>
            </div>
          </Button>
        </div>

        {/* Search and Filter */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'flex-end',
          }}>
            <div style={{ flex: 1 }}>
              <Input
                label="Search Conversations"
                placeholder="Search by name, subject, or message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Button
              variant={showArchived ? 'primary' : 'outline'}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? 'Show Active' : 'Show Archived'}
            </Button>
          </div>
        </Card>

        {/* Conversations List */}
        {filteredConversations.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '60px 40px' }}>
            <div style={{ marginBottom: '16px', color: theme.colors.textSecondary }}>
              <MessageSquareIcon width={48} height={48} />
            </div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '500',
              color: theme.colors.textPrimary,
              marginBottom: '8px',
            }}>
              {searchQuery ? 'No conversations found' : showArchived ? 'No archived conversations' : 'No messages yet'}
            </h3>
            <p style={{
              fontSize: '16px',
              color: theme.colors.textSecondary,
              marginBottom: '20px',
            }}>
              {searchQuery
                ? 'Try adjusting your search'
                : showArchived
                ? 'Archived conversations will appear here'
                : 'Start a conversation by clicking "New Message" above'}
            </p>
            {!searchQuery && !showArchived && (
              <Button onClick={() => window.location.href = '/messages/new'}>
                Send Your First Message
              </Button>
            )}
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredConversations.map((conversation) => (
              <Card
                key={conversation.id}
                style={{
                  backgroundColor: conversation.unread ? theme.colors.primaryLight : theme.colors.white,
                  border: conversation.unread ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onClick={() => window.location.href = `/messages/${conversation.id}`}
              >
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    backgroundColor: theme.colors.backgroundAlt,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative',
                  }}>
                    <UserIcon width={28} height={28} />
                    {conversation.unread && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.primary,
                        border: `2px solid ${theme.colors.white}`,
                      }} />
                    )}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '4px',
                        }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: conversation.unread ? '600' : '500',
                            color: theme.colors.textPrimary,
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {conversation.participantName}
                          </h3>
                          <Badge variant={getRoleBadgeVariant(conversation.participantRole)}>
                            {getRoleLabel(conversation.participantRole)}
                          </Badge>
                        </div>

                        {conversation.subject && (
                          <div style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            color: theme.colors.textPrimary,
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {conversation.subject}
                          </div>
                        )}
                      </div>

                      <div style={{
                        fontSize: '13px',
                        color: theme.colors.textTertiary,
                        flexShrink: 0,
                        marginLeft: '16px',
                      }}>
                        {conversation.lastMessageTime}
                      </div>
                    </div>

                    <p style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      margin: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {conversation.lastMessage}
                    </p>

                    {/* Actions */}
                    <div style={{
                      marginTop: '12px',
                      display: 'flex',
                      gap: '8px',
                    }}>
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (conversation.archived) {
                            handleUnarchive(conversation.id);
                          } else {
                            handleArchive(conversation.id);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <ArchiveIcon width={14} height={14} />
                          <span>{conversation.archived ? 'Unarchive' : 'Archive'}</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
